import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaEnvelope, FaSignInAlt, FaSpinner, FaGraduationCap } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { loginUser } from '../../services/authService';
import SEO from '../../components/common/SEO';
import PasswordInput from '../../components/common/PasswordInput';
import PageSection from '../../components/common/PageSection';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);

  const from = location.state?.from || null;

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      const { token, user } = await loginUser(form);
      login(token, user);
      toast.success(`Welcome back, ${user.name}!`);

      if (user.must_change_password) {
        navigate('/account/password', { replace: true, state: { from } });
      } else if (from) {
        navigate(from, { replace: true });
      } else if (user.role === 'student') {
        navigate('/dashboard/student', { replace: true });
      } else if (user.role === 'teacher') {
        navigate('/dashboard/teacher', { replace: true });
      } else {
        navigate('/dashboard/student', { replace: true });
      }
    } catch (err) {
      if (err.code === 'ACTIVATION_REQUIRED') {
        toast('Welcome, samurai! Activate your account to set a password.', { icon: '🗡️' });
        navigate('/activate', { state: { email: form.email, from } });
        return;
      }
      toast.error(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Login | Arvix Creative"
        description="Sign in to your Arvix Creative account"
      />
      <PageSection className="pt-24 sm:pt-28 md:pt-32 pb-12 md:pb-16">
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="w-full max-w-md">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100/50 shadow-lg p-6 sm:p-8 md:p-10">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-100 text-designer-primary text-xs font-bold tracking-widest mb-5">
                  <FaGraduationCap className="w-3.5 h-3.5" />
                  ARVIX ACADEMY
                </div>
                <h1 className="text-2xl font-bold text-designer-dark">Welcome Back</h1>
                <p className="text-gray-500 text-sm mt-1">Sign in to your Academy account</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-designer-primary/30 focus:border-designer-primary transition-all duration-200"
                    />
                  </div>
                </div>

                <PasswordInput
                  id="password"
                  name="password"
                  label="Password"
                  autoComplete="current-password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                />

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-r from-designer-primary to-red-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-red-500/30 hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="animate-spin w-4 h-4" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <FaSignInAlt className="w-4 h-4" />
                      Sign In
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 p-4 rounded-xl bg-red-50/70 border border-red-100 text-sm text-gray-600">
                <p className="font-semibold text-designer-dark">Creative Samurai student?</p>
                <p className="mt-1">
                  Your account is ready.{' '}
                  <Link to="/activate" state={{ email: form.email, from }} className="text-designer-primary font-semibold hover:underline">
                    Activate it or reset your password
                  </Link>{' '}
                  with your Student ID and phone number.
                </p>
              </div>

              <p className="text-center text-sm text-gray-500 mt-6">
                Don't have an account?{' '}
                <Link to="/register" className="text-designer-primary font-semibold hover:underline">
                  Join Academy
                </Link>
              </p>
              <p className="text-center text-xs text-gray-400 mt-3">
                <Link to="/academy" className="hover:text-designer-primary transition-colors">
                  ← Back to Arvix Academy
                </Link>
              </p>
            </div>
          </div>
        </div>
      </PageSection>
    </>
  );
};

export default Login;
